import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

export type KanbanStage = 'Yeni' | 'İletişim' | 'Görüşme' | 'Teklif' | 'Kapandı';

export interface FizboListing {
  id: string;
  listingNo: string;
  ownerName: string;
  phone: string;
  propertyType: string;
  transactionType: string;
  location: string;
  status: string;
  daysOld: number;
  fatigueScore: number;
  kanbanStage: KanbanStage;
  price: number;
  sqm: number | null;
  sellabilityScore: number;
  notes: string;
  priceHistory: { date: string; price: number }[];
  createdAt: string;
  listingDate: string | null;
  roomCount: string | null;
  user_id?: string;
}

interface DbRow {
  id: string;
  listing_no: string;
  owner_name: string;
  phone: string;
  property_type: string;
  transaction_type: string;
  location: string;
  status: string;
  days_old: number;
  fatigue_score: number;
  kanban_stage: string;
  price: number;
  sqm: number | null;
  sellability_score: number;
  notes: string | null;
  price_history: { date: string; price: number }[] | null;
  created_at: string;
  listing_date: string | null;
  room_count: string | null;
  user_id?: string;
}

function mapDbToListing(row: DbRow): FizboListing {
  return {
    id: row.id,
    listingNo: row.listing_no,
    ownerName: row.owner_name,
    phone: row.phone,
    propertyType: row.property_type,
    transactionType: row.transaction_type,
    location: row.location,
    status: row.status,
    daysOld: row.days_old,
    fatigueScore: row.fatigue_score,
    kanbanStage: (row.kanban_stage as KanbanStage) || 'Yeni',
    price: Number(row.price) || 0,
    sqm: row.sqm,
    sellabilityScore: row.sellability_score || 50,
    notes: row.notes || '',
    priceHistory: Array.isArray(row.price_history) ? row.price_history : [],
    createdAt: row.created_at ? row.created_at.split('T')[0] : '',
    listingDate: row.listing_date || null,
    roomCount: row.room_count || null,
    user_id: row.user_id,
  };
}

function generateListingNo(existing: FizboListing[]): string {
  const year = new Date().getFullYear();
  const prefix = `FZB-${year}-`;
  const nums = existing
    .map((l) => l.listingNo)
    .filter((n) => n.startsWith(prefix))
    .map((n) => parseInt(n.replace(prefix, ''), 10))
    .filter((n) => !isNaN(n));
  const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
  return `${prefix}${String(next).padStart(3, '0')}`;
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

export function useFizbo() {
  const { user } = useAuth();
  const [listings, setListings] = useState<FizboListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchListings = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);

    const { data, error: dbError } = await supabase
      .from('ep_fizbo_listings')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (dbError) {
      setError(dbError.message);
      setIsLoading(false);
      return;
    }

    setListings((data || []).map(mapDbToListing));
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const createAutoReminders = useCallback(
    async (listingId: string, listingDate: string | null, reminderOption: string, freeReminder?: { date: string; task: string }) => {
      if (!user) return;
      const reminders = [];

      if (reminderOption === 'free' && freeReminder) {
        reminders.push({
          user_id: user.id,
          task: freeReminder.task,
          due_date: freeReminder.date,
          due_time: '10:00',
          is_done: false,
          is_urgent: false,
          related_type: 'fizbo',
          related_id: listingId,
        });
      }

      if (reminderOption === 'auto' && listingDate) {
        reminders.push(
          {
            user_id: user.id,
            task: 'FİZBO ilanı 28 gün kontrolü - takip edilecek',
            due_date: addDays(listingDate, 28),
            due_time: '10:00',
            is_done: false,
            is_urgent: false,
            related_type: 'fizbo',
            related_id: listingId,
          },
          {
            user_id: user.id,
            task: 'FİZBO ilanı 58 gün kontrolü - güncelleme yapılacak',
            due_date: addDays(listingDate, 58),
            due_time: '10:00',
            is_done: false,
            is_urgent: false,
            related_type: 'fizbo',
            related_id: listingId,
          },
          {
            user_id: user.id,
            task: 'FİZBO ilanı 88 gün kontrolü - acil müdahale gerekli',
            due_date: addDays(listingDate, 88),
            due_time: '10:00',
            is_done: false,
            is_urgent: true,
            related_type: 'fizbo',
            related_id: listingId,
          }
        );
      }

      if (reminders.length > 0) {
        await supabase.from('ep_reminders').insert(reminders);
      }
    },
    [user]
  );

  const addListing = useCallback(
    async (form: {
      ownerName: string;
      phone: string;
      propertyType: string;
      transactionType: string;
      location: string;
      status: string;
      notes: string;
      price?: number;
      sqm?: number;
      roomCount?: string;
      listingDate?: string;
      reminderOption?: string;
      freeReminder?: { date: string; task: string };
    }): Promise<{ error: string | null; listing?: FizboListing }> => {
      if (!user) return { error: 'Oturum açmalısınız.' };

      const listingNo = generateListingNo(listings);
      const now = new Date().toISOString().split('T')[0];
      const price = form.price || 0;

      const dbRow = {
        listing_no: listingNo,
        owner_name: form.ownerName,
        phone: form.phone,
        property_type: form.propertyType,
        transaction_type: form.transactionType,
        location: form.location,
        status: form.status,
        days_old: 0,
        fatigue_score: 5,
        kanban_stage: 'Yeni',
        price,
        sqm: form.sqm || null,
        sellability_score: 70,
        notes: form.notes || '',
        price_history: [{ date: now, price }],
        user_id: user.id,
        listing_date: form.listingDate || null,
        room_count: form.roomCount || null,
      };

      const { data, error: dbError } = await supabase
        .from('ep_fizbo_listings')
        .insert(dbRow)
        .select()
        .single();

      if (dbError) {
        return { error: dbError.message };
      }

      const newListing = mapDbToListing(data as DbRow);
      setListings((prev) => [newListing, ...prev]);

      // Create reminders
      if (form.reminderOption && form.reminderOption !== 'none') {
        await createAutoReminders(newListing.id, newListing.listingDate || null, form.reminderOption, form.freeReminder);
      }

      return { error: null, listing: newListing };
    },
    [user, listings, createAutoReminders]
  );

  const updateListing = useCallback(
    async (
      id: string,
      updates: Partial<FizboListing>
    ): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Oturum açmalısınız.' };

      const dbUpdates: Record<string, unknown> = {};
      if (updates.ownerName !== undefined) dbUpdates.owner_name = updates.ownerName;
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
      if (updates.propertyType !== undefined) dbUpdates.property_type = updates.propertyType;
      if (updates.transactionType !== undefined) dbUpdates.transaction_type = updates.transactionType;
      if (updates.location !== undefined) dbUpdates.location = updates.location;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.daysOld !== undefined) dbUpdates.days_old = updates.daysOld;
      if (updates.fatigueScore !== undefined) dbUpdates.fatigue_score = updates.fatigueScore;
      if (updates.kanbanStage !== undefined) dbUpdates.kanban_stage = updates.kanbanStage;
      if (updates.price !== undefined) dbUpdates.price = updates.price;
      if (updates.sqm !== undefined) dbUpdates.sqm = updates.sqm;
      if (updates.sellabilityScore !== undefined) dbUpdates.sellability_score = updates.sellabilityScore;
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
      if (updates.priceHistory !== undefined) dbUpdates.price_history = updates.priceHistory;
      if (updates.listingDate !== undefined) dbUpdates.listing_date = updates.listingDate;
      if (updates.roomCount !== undefined) dbUpdates.room_count = updates.roomCount;

      const { error: dbError } = await supabase
        .from('ep_fizbo_listings')
        .update(dbUpdates)
        .eq('id', id)
        .eq('user_id', user.id);

      if (dbError) {
        return { error: dbError.message };
      }

      setListings((prev) =>
        prev.map((l) => (l.id === id ? { ...l, ...updates } : l))
      );
      return { error: null };
    },
    [user]
  );

  const deleteListing = useCallback(
    async (id: string): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Oturum açmalısınız.' };

      const { error: dbError } = await supabase
        .from('ep_fizbo_listings')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (dbError) {
        return { error: dbError.message };
      }

      setListings((prev) => prev.filter((l) => l.id !== id));
      return { error: null };
    },
    [user]
  );

  return {
    listings,
    isLoading,
    error,
    fetchListings,
    addListing,
    updateListing,
    deleteListing,
  };
}